// src/user/components/PhotoCaptureNode.js
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ActionButton from './ActionButton';
import StatusPill from '../../shared/components/pill/StatusPill';
import WorkflowStepInstruction from './WorkflowStepInstruction';
import { useWorkflowCache } from '../contexts/WorkflowCacheContext';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { compressImageForUpload } from '../utils/compressImageForUpload';
import {
  completeTaskCapture,
  createTaskCapture,
  fetchTaskCaptureDownload,
} from '../services/api/taskCaptures';

function buildDraftFromMedia(media, label, localPreviewUrl) {
  return {
    media_id: media.id,
    filename: media.filename,
    content_type: media.content_type,
    label: label || media.label,
    has_thumbnail: media.has_thumbnail,
    thumbnail_status: media.thumbnail_status,
    local_preview_url: localPreviewUrl || null,
  };
}

export default function PhotoCaptureNode({
  view,
  accessKey,
  taskId,
  previewMode = false,
  onNavigate,
  onFormValidChange,
  closeTask,
}) {
  const { graphic, heading, text, label, actions } = view;
  const stepId = view.id;
  const isRequired = view.required !== false;
  const reportLabel = (label || heading || 'Photo').trim();

  const { userProfile } = useContext(UserProfileContext);
  const { getPhotoDraft, setPhotoDraft } = useWorkflowCache();

  const onValidChangeRef = useRef(onFormValidChange);
  onValidChangeRef.current = onFormValidChange;

  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [captureDraft, setCaptureDraft] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const revokePreviewUrl = useCallback((url) => {
    if (url && typeof url === 'string' && url.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(url);
    }
  }, []);

  previewUrlRef.current = previewUrl;

  useEffect(() => {
    return () => {
      revokePreviewUrl(previewUrlRef.current);
    };
  }, [revokePreviewUrl]);

  const fieldEngineerId = userProfile?.field_engineer_id || null;
  const hasCapture = Boolean(captureDraft?.media_id || captureDraft?.local_preview_url);

  const updateValidity = useCallback(
    (captured) => {
      const valid = !isRequired || captured;
      onValidChangeRef.current?.(valid);
    },
    [isRequired]
  );

  const persistDraft = useCallback(
    (draft) => {
      setCaptureDraft(draft);
      if (previewMode) return;
      // `local_preview_url` is a blob: URL, which is only valid inside the document that
      // created it -- and this component revokes it on unmount. Writing it to storage left
      // a later visit reading back a dead URL and rendering a broken <img>. It stays in
      // component state for this mount; `media_id` is the handle that survives.
      const { local_preview_url: _localPreviewUrl, ...persistable } = draft || {};
      setPhotoDraft({ accessKey, taskId, stepId, capture: persistable });
    },
    [accessKey, taskId, stepId, previewMode, setPhotoDraft]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setUploadError(null);
      const stored = getPhotoDraft({ accessKey, taskId, stepId });
      if (!stored) {
        setCaptureDraft(null);
        setPreviewUrl(null);
        updateValidity(!isRequired);
        return;
      }

      setCaptureDraft(stored);

      if (previewMode) {
        // Admin preview never uploads, so an in-memory preview URL is all there is.
        if (stored.local_preview_url) setPreviewUrl(stored.local_preview_url);
        updateValidity(Boolean(stored.local_preview_url || stored.media_id));
        return;
      }

      if (!stored.media_id) {
        // Nothing reached the server, and any preview URL recorded on a previous visit
        // died with the document that made it, so there is nothing to show.
        updateValidity(false);
        return;
      }

      // Fetch from the server rather than trusting any stored preview URL: drafts written
      // by earlier versions still carry a revoked blob: URL alongside the media_id.
      setLoadingPreview(true);
      try {
        const variant = stored.has_thumbnail ? 'thumbnail' : 'original';
        const download = await fetchTaskCaptureDownload(accessKey, taskId, stored.media_id, {
          fieldEngineerId,
          variant,
        });
        if (!cancelled && download?.url) {
          setPreviewUrl(download.url);
        }
        if (!cancelled) updateValidity(true);
      } catch {
        if (stored.has_thumbnail) {
          try {
            const original = await fetchTaskCaptureDownload(accessKey, taskId, stored.media_id, {
              fieldEngineerId,
              variant: 'original',
            });
            if (!cancelled && original?.url) {
              setPreviewUrl(original.url);
            }
            if (!cancelled) updateValidity(true);
            return;
          } catch {
            // fall through
          }
        }
        if (!cancelled) {
          setUploadError('Could not load saved photo preview.');
          updateValidity(Boolean(stored.media_id));
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [accessKey, taskId, stepId, fieldEngineerId, previewMode, getPhotoDraft, updateValidity, isRequired]);

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadError(null);

    if (previewMode) {
      revokePreviewUrl(previewUrl);
      const localUrl = URL.createObjectURL(file);
      const draft = {
        local_preview_url: localUrl,
        filename: file.name,
        content_type: file.type,
        label: reportLabel,
      };
      setPreviewUrl(localUrl);
      persistDraft(draft);
      updateValidity(true);
      return;
    }

    setUploading(true);
    try {
      const prepared = await compressImageForUpload(file);
      revokePreviewUrl(previewUrl);
      const localUrl = URL.createObjectURL(prepared);

      const { media, upload } = await createTaskCapture(accessKey, taskId, {
        fieldEngineerId,
        media: {
          filename: prepared.name,
          content_type: prepared.type,
          byte_size: prepared.size,
          workflow_step_id: stepId,
          label: reportLabel,
        },
      });

      const putRes = await fetch(upload.url, {
        method: upload.method || 'PUT',
        headers: upload.headers || { 'Content-Type': prepared.type },
        body: prepared,
      });
      if (!putRes.ok) {
        throw new Error('Upload to storage failed');
      }

      await completeTaskCapture(accessKey, taskId, media.id, { fieldEngineerId });

      const draft = buildDraftFromMedia(media, reportLabel, localUrl);
      setPreviewUrl(localUrl);
      persistDraft(draft);
      updateValidity(true);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setUploadError(err.message || 'Failed to upload photo');
      updateValidity(!isRequired || Boolean(captureDraft?.media_id || captureDraft?.local_preview_url));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mt-3" id={`node-content-${stepId}`}>
      <WorkflowStepInstruction
        graphic={graphic}
        heading={heading}
        headingFallback={reportLabel}
        text={text}
        textClassName="mb-3"
      />

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div className="fw-semibold">{reportLabel}</div>
              {isRequired && (
                <div className="small text-muted">Required before you can continue</div>
              )}
            </div>
            {hasCapture && !uploading && (
              <StatusPill tone="success" size="touch" icon="bi-check-lg">Captured</StatusPill>
            )}
          </div>

          {loadingPreview && (
            <div className="text-muted small py-2">Loading saved photo…</div>
          )}

          {previewUrl && !loadingPreview && (
            <div className="text-center mb-3">
              <img
                src={previewUrl}
                alt={reportLabel}
                style={{ maxWidth: '100%', maxHeight: 320, height: 'auto', borderRadius: 8 }}
              />
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="d-none"
            onChange={handleFileSelected}
            disabled={uploading}
          />

          <button
            type="button"
            className="btn btn-outline-primary w-100"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Uploading…
              </>
            ) : (
              <>
                <i className="bi bi-camera me-2" aria-hidden="true" />
                {hasCapture ? 'Replace photo' : 'Take or choose photo'}
              </>
            )}
          </button>

          {previewMode && (
            <p className="small text-muted mt-2 mb-0">
              Preview mode — photos are not uploaded to the server.
            </p>
          )}

          {uploadError && (
            <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert">
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {(actions?.length > 0 || closeTask) && (
        <div className="row mt-4">
          {actions?.map((action, index) => (
            <div key={index} className="col-12 mb-2">
              <ActionButton
                action={action}
                fromStepId={view.id}
                actionIndex={index}
                taskId={taskId}
                accessKeyToken={accessKey}
                onNavigate={(targetViewId) => onNavigate(targetViewId)}
                disabled={isRequired && !hasCapture}
              />
            </div>
          ))}
          {closeTask && (
            <div className="col-12 mb-2">
              <ActionButton
                isCloseTask
                taskId={closeTask.taskId}
                accessKeyToken={closeTask.accessKeyToken}
                onTaskClosed={closeTask.onTaskClosed}
                afterClosePath={closeTask.afterClosePath}
                disabled={closeTask.disabled || (isRequired && !hasCapture)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
